// Storage of the pastoral formation record - see doc/pastoral-formation-concept.org §5.2.
//
// Like the team record and unlike Contract/Week/Focus, none of this is
// daily-cadence data: an encounter, an observation or a formation conversation
// lands on whatever day it lands on. So it all lives in *one* persistent file,
// the way gtdPath does, and not in the daily-note window - a new `formationPath`
// setting, default /roam/formation.org.
//
// Written as real Org headlines and plain lists, never a properties drawer, so
// the record is a first-class Org artifact any other tool (agenda, mobile
// client, Emacs) reads without knowing anything about organice:
//
//   * Pastorale Ausbildung :pastoral-formation:
//   ** Dimensions
//   *** Human :human:
//   learning to stop filling silences
//   *** Spiritual :spiritual:
//   *** Intellectual :intellectual:
//   *** Pastoral :pastoral:
//   ** Encounters
//   - [2026-08-09 Sun] M.B. :human:
//     - said :: "Das klingt, als hätten Sie das lange getragen."
//     - felt :: relieved when she cried, because it meant I could stop talking
//     - differently :: leave the silence longer instead of filling it
//     - avoided :: asking whether she still prays
//   ** Observed
//   - [2026-08-09 Sun] Kollegin R. :: said I was the only one who didn't look at my watch
//   ** Practised
//   - [2026-08-09 Sun] :time:error:
//   ** Formation conversations
//   - [2026-08-09 Sun] the funeral I rushed
//     - named :: I perform when I am frightened
//     - committed :: no notes at the next graveside address
//     - kept :: partly
//
// Three conventions worth stating, since all three are load-bearing on read:
//
//   - Dimensions and logged behaviours are identified by their *tag* (`:human:`,
//     `:time:`, ...), never by their title - so the labels in lib/formation.js
//     stay copy that can be reworded without resetting anybody's state.
//   - There is no TODO/DONE keyword anywhere in this node, unlike the team one.
//     Nothing here is a task to be completed, and marking a dimension DONE is a
//     sentence the concept does not want to be able to write (§5.2).
//   - Dates are inactive timestamps ([2026-08-09 Sun]) throughout: this is a
//     record of what happened, and none of it belongs in tomorrow's agenda.
//
// Read and written by line-level text surgery rather than a parseOrg/exportOrg
// round trip, for the same reason as lib/contract_org.js and lib/team_org.js:
// the file is the user's, and pushing it through the exporter could reformat
// content this node never touched. Surgery replaces the tagged node's *entire*
// subtree and leaves everything outside it alone - which also means nothing
// should be hand-added inside the node, since the next save regenerates it
// wholesale.

import {
  COMMITMENT_STATES,
  DIMENSION_KEYS,
  PRACTICE_KEYS,
  normalizeFormationState,
} from './formation';

export const FORMATION_TAG = 'pastoral-formation';
export const FORMATION_HEADLINE = 'Pastorale Ausbildung';

export const DIMENSIONS_TITLE = 'Dimensions';
export const ENCOUNTERS_TITLE = 'Encounters';
export const OBSERVED_TITLE = 'Observed';
export const PRACTISED_TITLE = 'Practised';
export const CONVERSATIONS_TITLE = 'Formation conversations';

// Separates who said it from what they said, on the one entry type whose primary
// line carries two fields (§4.3). Only emitted when there is a `who` - a remark
// logged without one is stored as the bare remark, which is also how a remark
// containing this separator and no `who` would read back wrongly. Accepted: the
// field is initials, and the alternative is escaping that makes the file worse
// for the human reading it in Emacs.
const WHO_SEPARATOR = ' :: ';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** A day key as an inactive org timestamp: 2026-08-09 -> [2026-08-09 Sun]. */
export const formatOrgDay = (key) => {
  const [year, month, day] = `${key}`.split('-').map(Number);
  const weekday = WEEKDAYS[new Date(year, month - 1, day).getDay()];

  return `[${key} ${weekday}]`;
};

// A leading date in either timestamp form, active or inactive - only the day is
// ever read back out, since nothing here records a time of day.
const LEADING_DAY_REGEX = /^[[<](\d{4}-\d{2}-\d{2})[^\]>]*[\]>]\s*/;

const headlineLevel = (line) => {
  const match = /^(\*+)\s/.exec(line);
  return match ? match[1].length : null;
};

const trailingTags = (text) => {
  const match = /:((?:[^\s:]+:)+)\s*$/.exec(text);
  return match ? match[1].split(':').filter(Boolean) : [];
};

/** A headline's own text: stars and trailing tags removed. */
const headlineTitle = (line) =>
  `${line}`
    .replace(/^\*+\s+/, '')
    .replace(/:(?:[^\s:]+:)+\s*$/, '')
    .trim();

/** Whatever is left of a list item once its trailing tags are taken off. */
const withoutTrailingTags = (text) => `${text}`.replace(/:(?:[^\s:]+:)+\s*$/, '').trim();

// Exactly one star and the formation tag: the user asked for the main node, and
// a deeper or differently-tagged headline of the same title is somebody else's.
const isFormationHeadline = (line) =>
  headlineLevel(line) === 1 &&
  trailingTags(line).some((tag) => tag.toLowerCase() === FORMATION_TAG);

/** Index one past the last line belonging to the subtree that starts at `index`. */
const endOfSubtreeIndex = (lines, index) => {
  let end = index + 1;
  while (end < lines.length) {
    const level = headlineLevel(lines[end]);
    if (level !== null && level <= 1) break;
    end += 1;
  }

  // A blank-line separator right before the next headline (or EOF) is spacing
  // between subtrees, not part of this one - excluding it from what gets
  // replaced means resaving does not eat that spacing line by line.
  while (end > index + 1 && lines[end - 1] === '') end -= 1;

  return end;
};

// A body line that starts with a star would read back as a headline and cut the
// subtree in half. Org's own escape for that is a leading comma.
const escapeBodyLine = (text) => (/^\s*\*/.test(text) ? `,${text}` : text);

const unescapeBodyLine = (text) => `${text}`.replace(/^,(\s*\*)/, '$1').trim();

const buildNode = (state) => {
  const { dimensions, encounters, observed, practised, conversations } = normalizeFormationState(
    state
  );

  const lines = [`* ${FORMATION_HEADLINE} :${FORMATION_TAG}:`];

  // Every section keeps its headline even while empty: the node is a form the
  // page fills in, and a section that vanishes when empty reads like a bug.
  lines.push(`** ${DIMENSIONS_TITLE}`);
  dimensions.forEach((dimension) => {
    lines.push(`*** ${dimension.label} :${dimension.key}:`);
    if (dimension.note) lines.push(escapeBodyLine(dimension.note));
  });

  lines.push(`** ${ENCOUNTERS_TITLE}`);
  encounters.forEach((entry) => {
    const tag = entry.dimension ? ` :${entry.dimension}:` : '';
    lines.push(`- ${formatOrgDay(entry.date)} ${entry.who}${tag}`.replace(/\s+$/, ''));
    lines.push(`  - said :: ${entry.said}`);
    if (entry.felt) lines.push(`  - felt :: ${entry.felt}`);
    if (entry.differently) lines.push(`  - differently :: ${entry.differently}`);
    if (entry.avoided) lines.push(`  - avoided :: ${entry.avoided}`);
  });

  lines.push(`** ${OBSERVED_TITLE}`);
  observed.forEach((entry) => {
    const said = entry.who ? `${entry.who}${WHO_SEPARATOR}${entry.what}` : entry.what;
    lines.push(`- ${formatOrgDay(entry.date)} ${said}`);
  });

  lines.push(`** ${PRACTISED_TITLE}`);
  practised.forEach((entry) => {
    lines.push(`- ${formatOrgDay(entry.date)} :${entry.keys.join(':')}:`);
  });

  lines.push(`** ${CONVERSATIONS_TITLE}`);
  conversations.forEach((entry) => {
    lines.push(`- ${formatOrgDay(entry.date)} ${entry.brought}`);
    if (entry.named) lines.push(`  - named :: ${entry.named}`);
    if (entry.committed) lines.push(`  - committed :: ${entry.committed}`);
    if (entry.kept) lines.push(`  - kept :: ${entry.kept}`);
  });

  return `${lines.join('\n')}\n`;
};

/**
 * The file contents with the formation record written as the formation-tagged
 * node - appended when the file has none yet, and the whole file is created when
 * there is no file at all (contents null).
 *
 * The node's entire subtree is replaced. Nothing outside it is touched.
 */
export const upsertFormationNode = (contents, state) => {
  const node = buildNode(state);

  if (contents === null || contents === undefined || contents === '') {
    return node;
  }

  const lines = contents.split('\n');
  const headlineIndex = lines.findIndex((line) => isFormationHeadline(line));

  if (headlineIndex === -1) {
    const terminated = contents.endsWith('\n') ? contents : `${contents}\n`;
    return `${terminated}\n${node}`;
  }

  const end = endOfSubtreeIndex(lines, headlineIndex);
  const nodeLines = node.split('\n');
  nodeLines.pop(); // the trailing '' left by node's closing newline

  return [...lines.slice(0, headlineIndex), ...nodeLines, ...lines.slice(end)].join('\n');
};

const LIST_ITEM_REGEX = /^-\s+(.*)$/;
const SUB_ITEM_REGEX = /^\s+-\s+(said|felt|differently|avoided|named|committed|kept)\s*::\s*(.*)$/;

const dayAndRest = (text) => {
  const match = LEADING_DAY_REGEX.exec(text);
  if (!match) return null;

  return { date: match[1], rest: text.slice(match[0].length).trim() };
};

const SECTION_BY_TITLE = {
  [DIMENSIONS_TITLE.toLowerCase()]: 'dimensions',
  [ENCOUNTERS_TITLE.toLowerCase()]: 'encounters',
  [OBSERVED_TITLE.toLowerCase()]: 'observed',
  [PRACTISED_TITLE.toLowerCase()]: 'practised',
  [CONVERSATIONS_TITLE.toLowerCase()]: 'conversations',
};

/**
 * The formation record out of its file, in the shape lib/formation.js works on,
 * or null when the file has no formation-tagged node at all (so the caller can
 * tell "no record yet" from "a record with nothing in it").
 *
 * Everything unrecognised inside the node is ignored rather than guessed at: the
 * file is hand-editable and one mangled line must not cost the rest.
 */
export const extractFormationFromFile = (contents) => {
  if (!contents) return null;

  const lines = contents.split('\n');
  const headlineIndex = lines.findIndex((line) => isFormationHeadline(line));
  if (headlineIndex === -1) return null;

  const end = endOfSubtreeIndex(lines, headlineIndex);

  const dimensions = [];
  const encounters = [];
  const observed = [];
  const practised = [];
  const conversations = [];

  let section = null;
  let currentDimension = null;

  for (let index = headlineIndex + 1; index < end; index += 1) {
    const line = lines[index];
    const level = headlineLevel(line);

    if (level !== null) {
      // A dimension row is a level-3 headline under Dimensions, keyed by its tag.
      if (level >= 3 && section === 'dimensions') {
        const key = trailingTags(line)
          .map((tag) => tag.toLowerCase())
          .find((tag) => DIMENSION_KEYS.includes(tag));

        currentDimension = key ? { key, note: '' } : null;
        if (currentDimension) dimensions.push(currentDimension);
        continue;
      }

      currentDimension = null;
      section = SECTION_BY_TITLE[headlineTitle(line).toLowerCase()] || null;
      continue;
    }

    if (!line.trim()) continue;

    if (section === 'dimensions') {
      // One free-text line per dimension (concept §4.1) - the first one wins, and
      // a comma-escaped leading star is unescaped on the way back in.
      if (currentDimension && !currentDimension.note) {
        currentDimension.note = unescapeBodyLine(line);
      }
      continue;
    }

    const subMatch = SUB_ITEM_REGEX.exec(line);
    if (subMatch) {
      const [, field, rawValue] = subMatch;
      const value = rawValue.trim();

      if (section === 'encounters' && encounters.length) {
        const last = encounters[encounters.length - 1];
        if (['said', 'felt', 'differently', 'avoided'].includes(field)) last[field] = value;
      } else if (section === 'conversations' && conversations.length) {
        const last = conversations[conversations.length - 1];
        if (field === 'named' || field === 'committed') last[field] = value;
        else if (field === 'kept') {
          const kept = value.toLowerCase();
          last.kept = COMMITMENT_STATES.includes(kept) ? kept : null;
        }
      }
      continue;
    }

    const itemMatch = LIST_ITEM_REGEX.exec(line);
    if (!itemMatch) continue;

    const dated = dayAndRest(itemMatch[1]);
    if (!dated) continue;

    if (section === 'encounters') {
      const tag = trailingTags(dated.rest)
        .map((value) => value.toLowerCase())
        .find((value) => DIMENSION_KEYS.includes(value));

      encounters.push({
        date: dated.date,
        who: withoutTrailingTags(dated.rest),
        // Filled in by the `- said ::` sub-item below; an entry that never gets
        // one is dropped by normalizeEncounters, which is the right outcome for
        // a headline with no words under it.
        said: '',
        felt: '',
        differently: '',
        avoided: '',
        dimension: tag || null,
      });
    } else if (section === 'observed') {
      const separator = dated.rest.indexOf(WHO_SEPARATOR);

      observed.push(
        separator === -1
          ? { date: dated.date, who: '', what: dated.rest }
          : {
              date: dated.date,
              who: dated.rest.slice(0, separator).trim(),
              what: dated.rest.slice(separator + WHO_SEPARATOR.length).trim(),
            }
      );
    } else if (section === 'practised') {
      const keys = trailingTags(dated.rest)
        .map((value) => value.toLowerCase())
        .filter((value) => PRACTICE_KEYS.includes(value));

      if (keys.length) practised.push({ date: dated.date, keys });
    } else if (section === 'conversations') {
      conversations.push({
        date: dated.date,
        brought: dated.rest,
        named: '',
        committed: '',
        kept: null,
      });
    }
  }

  return normalizeFormationState({
    dimensions,
    encounters,
    observed,
    practised,
    conversations,
  });
};
